import { BaseEdge, EdgeLabelRenderer, getBezierPath } from '@xyflow/react';
import { edgeStrokeStyle } from 'components/workflow/generic/flowkit/flowEdge';

export { edgeStrokeStyle } from 'components/workflow/generic/flowkit/flowEdge';

/**
 * workflowEdge.js — handle-anchored FLOW edge for the cell-workflow step graph.
 *
 * Mirrors the mission dependency edge (surfaces/cells/missionEdge.js) but with FLOW direction rather than
 * depends_on: each edge object (built in workflowGraph.js) sets source = the from-step and target = the
 * to-step, anchoring sourceHandle to the from-step's RIGHT (out) handle and targetHandle to the to-step's
 * LEFT (in) handle. React Flow resolves those handle anchor points and hands this component the ready-made
 * endpoints (sourceX/Y, targetX/Y) and handle sides, which we feed straight to getBezierPath — so the
 * bezier leaves the from-step's right edge and the arrowhead lands ON the NEXT (to) step's left edge,
 * reading left-to-right with the flow.
 *
 * The arrowhead marker (mc-graph-edge-arrow / -selected) is defined once in components/graphCanvas.js and
 * recolours with the edge via its selected variant — shared verbatim with the mission edge.
 */

function WorkflowEdge({
  sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, selected, data,
}) {
  const stroke = edgeStrokeStyle(selected);
  const markerEnd = `url(#${selected ? 'mc-graph-edge-arrow-selected' : 'mc-graph-edge-arrow'})`;
  const label = data && data.label;

  // Bezier curve between the two handle-anchored endpoints. The source/target Positions come straight
  // from the resolved handles (Right for the from-step, Left for the to-step), so the curve leaves and
  // enters each handle's facing side.
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={stroke} />
      {label && (
        <EdgeLabelRenderer>
          {/* White-chip label over the curve — matches the mission edge label (background #fff, small
              padding + radius). Darkens to the selected accent when the edge is selected. Only rendered
              when an edge carries a `when` condition, so most flow connectors stay clean and unlabelled. */}
          <div
            className={`nodrag nopan${selected ? ' selected' : ''}`}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 11,
              color: selected ? '#3d4756' : '#9aa3af',
              background: '#fff',
              padding: '0 3px',
              borderRadius: 3,
              pointerEvents: 'all',
              zIndex: selected ? 10 : 1,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default WorkflowEdge;
